import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

const styles = {
  container: { padding: '20px' },
  filters: {
    background: '#f8f9fa',
    padding: '15px',
    borderRadius: '5px',
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '15px',
  },
  tableContainer: { marginTop: '20px', overflowX: 'auto' },
  table: { width: '100%', minWidth: '1050px', borderCollapse: 'collapse' },
  th: {
    border: '1px solid #ddd',
    padding: '10px 12px',
    background: '#f4f4f4',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '0.9rem',
  },
  td: { border: '1px solid #ddd', padding: '10px 12px', textAlign: 'right' },
  tdLeft: { textAlign: 'left' },
  input: { padding: '6px 8px', fontSize: '0.9rem', width: '120px' },
  select: { padding: '6px 8px', fontSize: '0.9rem', minWidth: '80px' },
  sectionTitle: { fontSize: '1.1rem', fontWeight: 'bold', color: '#333', marginBottom: 0 },
  sectionTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
    flexWrap: 'wrap',
    gap: '10px',
  },
  bulkButton: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #2563eb',
    background: '#2563eb',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  excelButton: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #28a745',
    background: '#28a745',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  infoBox: {
    marginBottom: '20px',
    background: '#f0f7ff',
    border: '1px solid #2563eb',
    padding: '12px 16px',
    borderRadius: '5px',
    fontSize: '0.9rem',
    lineHeight: 1.5,
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
  },
  modal: {
    width: '92%',
    maxWidth: '560px',
    background: '#fff',
    borderRadius: '10px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
    overflow: 'hidden',
  },
  modalHeader: {
    padding: '14px 16px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  modalBody: { padding: '16px' },
  modalFooter: {
    padding: '12px 16px',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  button: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    background: '#fff',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  primaryButton: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #2563eb',
    background: '#2563eb',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
};

const formatCurrency = (n) => `${(Number(n) || 0).toLocaleString()} 원`;
const formatNumber = (n) => `${(Number(n) || 0).toLocaleString()}`;

export default function WeeklyAllowancePage() {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [year, setYear] = useState(currentYear);
  const [baseMonth, setBaseMonth] = useState(currentMonth);
  const [applyAdditionalHeadcount, setApplyAdditionalHeadcount] = useState(true);
  const [laborData, setLaborData] = useState({ items: [] });
  const [data, setData] = useState({ year: currentYear, baseMonth: currentMonth, items: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [localConfig, setLocalConfig] = useState({}); // { projectId: { endMonth,weeklyHours,hourlyRate,weeksPerMonth } }
  const [saving, setSaving] = useState({});
  const [editingProject, setEditingProject] = useState(null); // { projectId, projectName }
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkInput, setBulkInput] = useState({ endMonth: '', weeklyHours: '', hourlyRate: '', weeksPerMonth: '' });
  const [bulkSaving, setBulkSaving] = useState(false);

  const editing = editingProject
    ? (localConfig[editingProject.projectId] || { endMonth: 12, weeklyHours: 0, hourlyRate: 0, weeksPerMonth: 4 })
    : null;

  const fetchSummary = useCallback(async () => {
    if (!year) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/weekly-allowance-budget/summary', { params: { year, baseMonth } });
      setData(res.data);
      const next = {};
      (res.data.items || []).forEach((item) => {
        next[item.projectId] = {
          endMonth: item.endMonth,
          weeklyHours: item.weeklyHours || item.defaultWeeklyHours || 0,
          hourlyRate: item.hourlyRate || item.defaultHourlyRate || 0,
          weeksPerMonth: item.weeksPerMonth || 4,
        };
      });
      setLocalConfig(next);
    } catch (err) {
      setError(err.response?.data?.message || '주휴수당 예산 요약을 불러오는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [year, baseMonth]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (!applyAdditionalHeadcount || !year) return;
    api.get('/labor-budget/summary', { params: { year, baseMonth } })
      .then((res) => setLaborData(res.data))
      .catch(() => setLaborData({ items: [] }));
  }, [applyAdditionalHeadcount, year, baseMonth]);

  const laborByProject = useMemo(() => {
    const map = {};
    (laborData.items || []).forEach((i) => { map[i.projectId] = i; });
    return map;
  }, [laborData.items]);

  const handleSave = async (projectId, payload) => {
    setSaving((s) => ({ ...s, [projectId]: true }));
    try {
      await api.put('/weekly-allowance-budget', {
        year,
        projectId,
        endMonth: Math.min(12, Math.max(1, Number(payload.endMonth) || 12)),
        weeklyHours: Number(payload.weeklyHours) || 0,
        hourlyRate: Number(payload.hourlyRate) || 0,
        weeksPerMonth: Number(payload.weeksPerMonth) || 4,
      });
      await fetchSummary();
    } catch (err) {
      setError(err.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setSaving((s) => ({ ...s, [projectId]: false }));
    }
  };

  const openEditModal = (item) => setEditingProject({ projectId: item.projectId, projectName: item.projectName });
  const closeEditModal = () => setEditingProject(null);

  const openBulkModal = () => setBulkModalOpen(true);
  const closeBulkModal = () => {
    setBulkModalOpen(false);
    setBulkInput({ endMonth: '', weeklyHours: '', hourlyRate: '', weeksPerMonth: '' });
  };

  const handleBulkSave = async () => {
    setBulkSaving(true);
    setError('');
    try {
      for (const item of data.items) {
        const cfg = localConfig[item.projectId] || {
          endMonth: item.endMonth,
          weeklyHours: item.weeklyHours || item.defaultWeeklyHours || 0,
          hourlyRate: item.hourlyRate || item.defaultHourlyRate || 0,
          weeksPerMonth: item.weeksPerMonth || 4,
        };
        const endMonth = bulkInput.endMonth !== '' && bulkInput.endMonth !== undefined
          ? Math.min(12, Math.max(1, Number(bulkInput.endMonth) || 12))
          : cfg.endMonth;
        const weeklyHours = bulkInput.weeklyHours !== '' && bulkInput.weeklyHours !== undefined
          ? Number(bulkInput.weeklyHours) || 0
          : (cfg.weeklyHours ?? item.defaultWeeklyHours ?? 0);
        const hourlyRate = bulkInput.hourlyRate !== '' && bulkInput.hourlyRate !== undefined
          ? Number(String(bulkInput.hourlyRate).replace(/[^\d]/g, '')) || 0
          : (cfg.hourlyRate ?? item.defaultHourlyRate ?? 0);
        const weeksPerMonth = bulkInput.weeksPerMonth !== '' && bulkInput.weeksPerMonth !== undefined
          ? Math.min(5, Math.max(1, Number(bulkInput.weeksPerMonth) || 4))
          : (cfg.weeksPerMonth ?? 4);

        await api.put('/weekly-allowance-budget', {
          year,
          projectId: item.projectId,
          endMonth,
          weeklyHours,
          hourlyRate,
          weeksPerMonth,
        });
      }
      await fetchSummary();
      closeBulkModal();
    } catch (err) {
      setError(err.response?.data?.message || '일괄 저장에 실패했습니다.');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleExcelExport = useCallback(async () => {
    try {
      const res = await api.get('/weekly-allowance-budget/export', {
        params: { year, baseMonth, applyAdditionalHeadcount: applyAdditionalHeadcount ? 'true' : 'false' },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `weekly_allowance_budget_${year}_${baseMonth}월.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || '주휴수당 예산 엑셀 출력에 실패했습니다.');
    }
  }, [year, baseMonth, applyAdditionalHeadcount]);

  return (
    <div style={styles.container}>
      <h2>주휴수당 예산관리</h2>
      <div style={styles.infoBox}>
        사업별로 <strong>사업 종료월</strong>, <strong>주당발생시간</strong>, <strong>시간당 단가</strong>, <strong>월 지급횟수</strong>를 설정하세요.
        <br />
        향후 예산은 <strong>기준 인원(선택한 월)</strong>을 종료월까지 유지한다고 가정하고, <strong>(주당발생시간 × 시간당 단가 × 월 지급횟수)</strong>로 월 필요 예산을 추정합니다.
        <br />
        <strong>추가인원 적용</strong> 시: 인건비 예산 관리의 &quot;추가고용여력&quot; 값을 사용하여, 해당 추가 인원의 주휴수당을 남은 개월 동안 필요로 하는 예산을 합계(필요) 예산에 더해 표시합니다. 인건비 잔여 예산으로 추가 고용 가능한 인원을 주휴수당 예산에도 반영하려 할 때 사용합니다.
      </div>

      <div style={styles.filters}>
        <div style={{ display: 'flex', gap: 15, alignItems: 'center', flexWrap: 'wrap' }}>
          <label>
            연도
            <input
              type="number"
              style={{ ...styles.input, marginLeft: '8px' }}
              value={year}
              onChange={(e) => setYear(Number(e.target.value) || currentYear)}
              min="2020"
              max="2030"
            />
          </label>
          <label>
            기준 인원
            <select style={{ ...styles.select, marginLeft: '8px' }} value={baseMonth} onChange={(e) => setBaseMonth(Number(e.target.value))}>
              {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                <option key={m} value={m}>{m}월</option>
              ))}
            </select>
          </label>
          <label>
            추가인원 적용
            <select style={{ ...styles.select, marginLeft: '8px' }} value={applyAdditionalHeadcount ? 'apply' : 'no'} onChange={(e) => setApplyAdditionalHeadcount(e.target.value === 'apply')}>
              <option value="no">미적용</option>
              <option value="apply">적용</option>
            </select>
          </label>
        </div>
      </div>

      {loading && <p>조회 중...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}

      {!loading && data.items && data.items.length > 0 && (
        <div style={styles.tableContainer}>
          <div style={styles.sectionTitleRow}>
            <h3 style={styles.sectionTitle}>사업별 주휴수당 예산 및 집계</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" style={styles.bulkButton} onClick={openBulkModal} disabled={loading}>
                일괄입력
              </button>
              <button type="button" style={styles.excelButton} onClick={handleExcelExport} disabled={loading}>
                엑셀 출력
              </button>
            </div>
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, ...styles.tdLeft, width: applyAdditionalHeadcount ? '12.88%' : '12%' }}>사업명</th>
                <th style={{ ...styles.th, width: applyAdditionalHeadcount ? '8.28%' : '9%' }}>사업 종료월</th>
                <th style={{ ...styles.th, width: applyAdditionalHeadcount ? '11.04%' : '10%' }}>시간당 단가(원)</th>
                <th style={{ ...styles.th, width: applyAdditionalHeadcount ? '8.28%' : '9%' }}>{baseMonth}월 인원(명)</th>
                <th style={{ ...styles.th, width: applyAdditionalHeadcount ? '6.28%' : '9.75%' }}>남은개월</th>
                <th style={{ ...styles.th, width: applyAdditionalHeadcount ? '14.88%' : '18.75%' }}>사용 예산</th>
                <th style={{ ...styles.th, width: applyAdditionalHeadcount ? '11.04%' : '12%' }}>향후 예산</th>
                {applyAdditionalHeadcount && <th style={{ ...styles.th, width: '8%' }}>추가인원(명)</th>}
                <th style={{ ...styles.th, width: applyAdditionalHeadcount ? '11.04%' : '12%' }}>합계(필요) 예산</th>
                <th style={{ ...styles.th, width: applyAdditionalHeadcount ? '8.28%' : '7.5%' }}>입력</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => {
                const cfg = localConfig[item.projectId] || {
                  endMonth: item.endMonth,
                  weeklyHours: item.weeklyHours || item.defaultWeeklyHours || 0,
                  hourlyRate: item.hourlyRate,
                  weeksPerMonth: item.weeksPerMonth,
                };
                const laborItem = laborByProject[item.projectId];
                const additionalHeadcount = laborItem ? (laborItem.additionalHeadcount || 0) : 0;
                const displayTotal = applyAdditionalHeadcount && additionalHeadcount > 0 && (item.remainingMonthsCount || 0) > 0
                  ? item.totalNeededBudget + additionalHeadcount * item.perPersonPerMonth * item.remainingMonthsCount
                  : item.totalNeededBudget;
                return (
                  <tr key={item.projectId}>
                    <td style={{ ...styles.td, ...styles.tdLeft, width: applyAdditionalHeadcount ? '12.88%' : '12%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.projectName}</td>
                    <td style={{ ...styles.td, width: applyAdditionalHeadcount ? '8.28%' : '9%' }}>{cfg.endMonth}월</td>
                    <td style={{ ...styles.td, width: applyAdditionalHeadcount ? '11.04%' : '10%' }}>{formatNumber(cfg.hourlyRate || item.defaultHourlyRate || 0)}</td>
                    <td style={{ ...styles.td, width: applyAdditionalHeadcount ? '8.28%' : '9%' }}>{item.baseHeadcount ?? 0}</td>
                    <td style={{ ...styles.td, width: applyAdditionalHeadcount ? '6.28%' : '9.75%' }}>{item.remainingMonthsCount ?? 0}</td>
                    <td style={{ ...styles.td, width: applyAdditionalHeadcount ? '14.88%' : '18.75%' }}>
                      {formatCurrency(item.usedCumul)}{' '}
                      <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                        (1~{item.asOfMonth || 0}월)
                      </span>
                    </td>
                    <td style={{ ...styles.td, width: applyAdditionalHeadcount ? '11.04%' : '12%' }}>{formatCurrency(item.futureTotal)}</td>
                    {applyAdditionalHeadcount && (
                      <td style={{ ...styles.td, width: '8%' }}>{additionalHeadcount > 0 ? formatNumber(additionalHeadcount) : '-'}</td>
                    )}
                    <td style={{ ...styles.td, width: applyAdditionalHeadcount ? '11.04%' : '12%' }}>{formatCurrency(displayTotal)}</td>
                    <td style={{ ...styles.td, textAlign: 'center', width: applyAdditionalHeadcount ? '8.28%' : '7.5%' }}>
                      <button
                        type="button"
                        style={{ ...styles.button, padding: '4px 8px', fontSize: '0.8rem', lineHeight: 1 }}
                        onClick={() => openEditModal(item)}
                      >
                        입력
                      </button>
                      {saving[item.projectId] && (
                        <div style={{ marginTop: 6, fontSize: '0.85rem', color: '#666', textAlign: 'center' }}>저장 중...</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {bulkModalOpen && (
        <div style={styles.modalOverlay} onClick={closeBulkModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={{ fontWeight: 700 }}>사업별 주휴수당 예산 일괄입력</div>
              <button type="button" style={styles.button} onClick={closeBulkModal}>닫기</button>
            </div>
            <div style={styles.modalBody}>
              <div style={{ fontSize: '0.9rem', color: '#374151', marginBottom: 12 }}>
                입력한 항목만 현재 목록의 모든 사업에 일괄 적용됩니다. 비워 둔 항목은 각 사업의 기존값이 그대로 유지됩니다.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>사업 종료월</span>
                  <select
                    style={{ ...styles.select, width: '100%' }}
                    value={bulkInput.endMonth}
                    onChange={(e) => setBulkInput((p) => ({ ...p, endMonth: e.target.value }))}
                  >
                    <option value=""> </option>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                      <option key={m} value={m}>{m}월</option>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>주당발생시간</span>
                  <input
                    type="number"
                    style={{ ...styles.input, width: '100%' }}
                    value={bulkInput.weeklyHours}
                    onChange={(e) => setBulkInput((p) => ({ ...p, weeklyHours: e.target.value }))}
                    min={0}
                    step="0.5"
                    placeholder="입력 시 전체 적용, 비워두면 기존값 유지"
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>시간당 단가(원)</span>
                  <input
                    type="text"
                    style={{ ...styles.input, width: '100%' }}
                    value={bulkInput.hourlyRate}
                    onChange={(e) => setBulkInput((p) => ({ ...p, hourlyRate: e.target.value }))}
                    inputMode="numeric"
                    placeholder="입력 시 전체 적용, 비워두면 기존값 유지"
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>월 지급횟수</span>
                  <input
                    type="number"
                    style={{ ...styles.input, width: '100%' }}
                    value={bulkInput.weeksPerMonth}
                    onChange={(e) => setBulkInput((p) => ({ ...p, weeksPerMonth: e.target.value }))}
                    min={1}
                    max={5}
                    step={1}
                    placeholder="입력 시 전체 적용, 비워두면 기존값 유지"
                  />
                </label>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button type="button" style={styles.button} onClick={closeBulkModal}>취소</button>
              <button
                type="button"
                style={styles.primaryButton}
                onClick={handleBulkSave}
                disabled={bulkSaving}
              >
                {bulkSaving ? '저장 중...' : '일괄 저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingProject && editing && (
        <div style={styles.modalOverlay} onClick={closeEditModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <div style={{ fontWeight: 700 }}>자료입력</div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: 2 }}>{editingProject.projectName}</div>
              </div>
              <button type="button" style={styles.button} onClick={closeEditModal}>닫기</button>
            </div>
            <div style={styles.modalBody}>
              <div style={{ fontSize: '0.9rem', color: '#374151', marginBottom: 12 }}>
                기본값으로 시간당 단가는 사업의 <strong>연차수당(원)</strong> 값을 불러옵니다. 필요 시 수정할 수 있습니다.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>사업 종료월</span>
                  <select
                    style={{ ...styles.select, width: '100%' }}
                    value={editing.endMonth}
                    onChange={(e) => {
                      setLocalConfig((prev) => ({
                        ...prev,
                        [editingProject.projectId]: { ...(prev[editingProject.projectId] || {}), endMonth: Number(e.target.value) || 12 },
                      }));
                    }}
                  >
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                      <option key={m} value={m}>{m}월</option>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>주당발생시간</span>
                  <input
                    type="number"
                    style={{ ...styles.input, width: '100%' }}
                    value={editing.weeklyHours}
                    onChange={(e) => {
                      setLocalConfig((prev) => ({
                        ...prev,
                        [editingProject.projectId]: { ...(prev[editingProject.projectId] || {}), weeklyHours: Number(e.target.value) || 0 },
                      }));
                    }}
                    min={0}
                    step="0.5"
                    placeholder="0"
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>시간당 단가(원)</span>
                  <input
                    type="text"
                    style={{ ...styles.input, width: '100%' }}
                    value={formatNumber(editing.hourlyRate)}
                    onChange={(e) => {
                      const digits = (e.target.value || '').replace(/[^\d]/g, '');
                      const nextVal = digits ? Number(digits) : 0;
                      setLocalConfig((prev) => ({
                        ...prev,
                        [editingProject.projectId]: { ...(prev[editingProject.projectId] || {}), hourlyRate: nextVal },
                      }));
                    }}
                    inputMode="numeric"
                    placeholder="0"
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>월 지급횟수</span>
                  <input
                    type="number"
                    style={{ ...styles.input, width: '100%' }}
                    value={editing.weeksPerMonth}
                    onChange={(e) => {
                      setLocalConfig((prev) => ({
                        ...prev,
                        [editingProject.projectId]: { ...(prev[editingProject.projectId] || {}), weeksPerMonth: Number(e.target.value) || 4 },
                      }));
                    }}
                    min={1}
                    max={5}
                    step={1}
                  />
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>기본값은 4입니다.</div>
                </label>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button type="button" style={styles.button} onClick={closeEditModal}>취소</button>
              <button
                type="button"
                style={styles.primaryButton}
                onClick={async () => {
                  await handleSave(editingProject.projectId, editing);
                  closeEditModal();
                }}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
